import { captureException } from './error-tracker';
import { getLocalStorageObject, setLocalStorageObject } from './localStorage';

/**
 * LogLevel enum for application logging with priority levels
 * Similar to Winston's RFC5424 logging levels
 * Lower values indicate higher priority
 *
 * @see getUserLogAndNotifyLevel
 *
 * @warning Not really used/elsewhere properly yet, just needed a sane place to put the enum
 */
export enum LogLevel {
  /* Critical errors that require immediate attention */
  Error = 3,
  /* Important warnings that should be addressed */
  Warning = 4,
  /* General operational information */
  Info = 5,
  /* Detailed debug information for developers */
  Debug = 6,
  /* Extra verbose information for troubleshooting */
  Verbose = 7,
}

type DebugTemplatedParams = {
  /** Expand the console.group (e.g, show `debug` data by default) */
  expand?: boolean;
  /** Prefix in a specific HTML color? */
  color?: string;
  /** Prefix in a specific CSS font-weight? */
  fontWeight?: string;
  /** Prefix in a specific CSS font-style? */
  fontStyle?: string;
  /** Override the templates default icon */
  icon?: string;
  /** Any non-typed param will be dumped to console */
  [key: string]: any;
  /** Will print in format ala "{topic} / {subtopic}:", helpful for noisy consoles */
  subtopic?: string;
};

/**
 * Verbose/debug toasting
 */
const ENABLE_VERBOSE_TOAST_KEY = '__TIMELY_DEV__ENABLE_VERBOSE_TOAST';
const enableVerboseToastInStore = getLocalStorageObject<boolean>(ENABLE_VERBOSE_TOAST_KEY);
let enableVerboseToast = enableVerboseToastInStore ?? process.env.NODE_ENV === 'development';

if (enableVerboseToast) {
  console.log(
    '%c🐛 Verbose toasts enabled. Control via DevTools->Show verbose toasts',
    'font-weight: bold'
  );
}

export const setUseVerboseToastsInLocalStorage = (enable: boolean) => {
  enableVerboseToast = enable;
  setLocalStorageObject<boolean>(ENABLE_VERBOSE_TOAST_KEY, enable);
};
export const getUserVerboseToastsFromLocalStorage = () => enableVerboseToast;

/**
 * Debug logging to console.
 */
const ENABLED_KEY = '__TIMELY_DEV__ENABLE_DEBUG_LOGGING';
const enableDebugLoggingInStore = getLocalStorageObject<boolean>(ENABLED_KEY);
let enableDebugLogging = enableDebugLoggingInStore ?? process.env.NODE_ENV === 'development';
if (enableDebugLogging) {
  console.log('%c🐛 Debug logging enabled', 'font-weight: bold');
}

export const isStackTraceLineRelevant = (line: string): boolean => {
  return (
    !line.includes('node_modules') &&
    !line.includes('js/vendors-') &&
    !line.startsWith('__webpack_') &&
    !line.startsWith('@') &&
    !line.startsWith('options.factory@')
  );
};

export const setEnableDebugLogging = (enable: boolean) => {
  enableDebugLogging = enable;
  setLocalStorageObject<boolean>(ENABLED_KEY, enable);
};
export const getEnableDebugLogging = () => enableDebugLogging;

const debug = {
  group(...args: Parameters<typeof console.group>) {
    if (!enableDebugLogging) {
      return;
    }
    console.group(...args);
  },
  groupCollapsed(...args: Parameters<typeof console.group>) {
    if (!enableDebugLogging) {
      return;
    }
    console.groupCollapsed(...args);
  },
  groupEnd(...args: Parameters<typeof console.groupEnd>) {
    if (!enableDebugLogging) {
      return;
    }
    console.groupEnd(...args);
  },
  log(...args: Parameters<typeof console.log>) {
    if (!enableDebugLogging) {
      return;
    }
    console.log(...args);
  },
  warn(...args: Parameters<typeof console.warn>) {
    if (!enableDebugLogging) {
      return;
    }
    console.warn(...args);
  },
  info(...args: Parameters<typeof console.info>) {
    if (!enableDebugLogging) {
      return;
    }
    console.info(...args);
  },
  error(...args: Parameters<typeof console.error>) {
    if (!enableDebugLogging) {
      return;
    }
    console.error(...args);
  },
  dir(...args: Parameters<typeof console.dir>) {
    if (!enableDebugLogging) {
      return;
    }
    console.dir(...args);
  },
  here() {
    if (!enableDebugLogging) {
      return;
    }

    // eslint-disable-next-line no-debugger
    debugger;
  },
  assert(condition: boolean, message: string, ...optionalParams: Array<any>) {
    try {
      console.assert(condition, message, ...optionalParams);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        throw e;
      } else {
        // We want failed assertions to show up in Sentry in prod
        captureException(e);
      }
    }
  },
  stack(...args: Parameters<typeof console.group>) {
    if (!enableDebugLogging) {
      return;
    }

    console.group(...args);
    console.log(new Error().stack!.split('\n').filter(isStackTraceLineRelevant).join('\n'));
    console.groupEnd();
  },
  /** For consistency of debug messages across files, debug.template.ADD_YOURS_HERE let's you easily style things for clarify in DevTools console */
  template: {
    /** ONLY returns in development */
    devEnvOnly(message: string, params?: DebugTemplatedParams) {
      if (process.env.NODE_ENV !== 'development') return;
      return logTemplate('DevEnv', message, { color: '#888', icon: '🛠️', ...params });
    },
    rateLimit(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Rate limit', message, { color: 'lime', icon: '🕒', ...params });
    },
    auth(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Auth', message, { color: 'green', icon: '🔑', ...params });
    },
    ai(message: string, params?: DebugTemplatedParams) {
      return logTemplate('AI', message, { color: 'red', icon: '🤖', ...params });
    },
    memory(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Memory', message, { color: 'pink', icon: '🧠', ...params });
    },
    configuration(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Config', message, { color: 'navy', icon: '⚙️', ...params });
    },
    hour(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Hour', message, { color: 'orange', icon: '⏱️', ...params });
    },
    notifications(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Notifications', message, { color: 'blue', icon: '📬', ...params });
    },
    pusher(message: string, params?: DebugTemplatedParams & { origin?: 'timely' | 'TIC' }) {
      const origin = params?.origin ? params.origin : 'timely';
      const icon = params?.icon || '🔌';
      return logTemplate(`Pusher/${origin}`, message, { color: 'orange', icon, ...params });
    },
    sentry(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Sentry', message, { color: 'purple', icon: '🪵', ...params });
    },
    analytics(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Analytics', message, { color: 'blue', icon: '📊', ...params });
    },
    todo(message: string, params?: DebugTemplatedParams) {
      return logTemplate('TODO', message, { color: 'red', icon: '🚧', ...params });
    },
    infra(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Infra', message, { color: 'red', icon: '🏗️', ...params });
    },
    integration(message: string, params?: DebugTemplatedParams) {
      return logTemplate('Integration', message, { color: 'purple', icon: '🌐', ...params });
    },
  },
};

const logTemplate = (topic: string, message: string, params: DebugTemplatedParams = {}) => {
  if (!enableDebugLogging) {
    return;
  }
  const {
    expand = false,
    color = 'orange',
    fontWeight = 'bold',
    fontStyle = 'normal',
    icon = '🪵',
    subtopic,
    ...rest
  } = params || {};

  const topicWithSubtopic = subtopic ? `${topic} / ${subtopic}` : topic;
  const groupWith = expand ? console.group : console.groupCollapsed;
  groupWith(
    `%c${icon} ${topicWithSubtopic}:%c ${message}`,
    `font-weight: ${fontWeight}; font-style: ${fontStyle}; color: ${color};`,
    'reset: all; font-weight: normal'
  );
  if (rest) console.info(rest);
  console.groupEnd();
};

export default debug;
